import { createSocketAuth } from '@/lib/socketAuth'
import { optionalRefreshResources, refreshResources, type RefreshResource, type RefreshCoordinator } from './refreshCoordinator'

/** One connection per mounted host. No timer fetches business data here. */
export function startRefreshConnection(url: string, invalidate: RefreshCoordinator['invalidate']) {
  let stopped = false
  let attempts = 0
  let socket: WebSocket | undefined
  let auth: ReturnType<typeof createSocketAuth> | undefined
  let retry: ReturnType<typeof setTimeout> | undefined
  let watchdog: ReturnType<typeof setTimeout> | undefined
  let lastFrame = Date.now()
  // 后端拒了整条订阅（不认识新资源）之后，这个连接的余生只订阅它一定认识的资源
  let withoutOptional = false
  const resources = () => Object.keys(refreshResources)
    .filter((r) => !withoutOptional || !optionalRefreshResources.includes(r as RefreshResource))
  const connect = () => {
    if (stopped || !navigator.onLine) return
    const ws = new WebSocket(url)
    socket = ws
    const current = () => !stopped && socket === ws
    let subscribed = false
    const lease = createSocketAuth(ws, () => {
      if (current()) ws.send(JSON.stringify({ type: 'subscribe', resources: resources() }))
    })
    auth = lease
    const watch = () => {
      clearTimeout(watchdog)
      watchdog = setTimeout(() => { if (current()) ws.close(4000, 'Connection stalled') }, 60_000)
    }
    watch()
    ws.onopen = () => { void lease.open() }
    ws.onmessage = event => {
      if (!current()) return
      lastFrame = Date.now()
      watch()
      let frame: { type?: string; resource?: string }
      try { frame = JSON.parse(String(event.data)) } catch { return }
      if (!frame || typeof frame !== 'object') return
      if (lease.handle(frame)) return
      if (frame.type === 'ping') { ws.send(JSON.stringify({ type: 'pong' })); return }
      if (frame.type === 'subscribed') { attempts = 0; subscribed = true; return }
      if (frame.type === 'resync') { invalidate(undefined, 'reconnect'); return }
      if (frame.type === 'resource.invalidated' && frame.resource && Object.hasOwn(refreshResources, frame.resource)) {
        invalidate([frame.resource as RefreshResource], 'change')
      }
    }
    ws.onclose = (event?: CloseEvent) => {
      lease.dispose()
      if (!current()) return
      clearTimeout(watchdog)
      if (!subscribed && !withoutOptional && event?.code === 1008 && event.reason === 'Invalid subscription') {
        withoutOptional = true
        retry = setTimeout(connect, 0)
        return
      }
      if (navigator.onLine) {
        const delay = attempts >= 10 ? 300_000 : Math.min(30_000, 1000 * 2 ** attempts)
        attempts++
        retry = setTimeout(connect, delay + Math.random() * 1000)
      }
    }
  }
  const resume = () => {
    if (stopped || !navigator.onLine) return
    if (socket && socket.readyState < 2 && Date.now() - lastFrame < 60_000) return
    clearTimeout(retry)
    const old = socket
    socket = undefined
    auth?.dispose()
    old?.close()
    connect()
  }
  const offline = () => { clearTimeout(retry); socket?.close(4000, 'Offline') }
  const stop = () => {
    stopped = true
    clearTimeout(retry); clearTimeout(watchdog)
    auth?.dispose(); socket?.close()
    window.removeEventListener('online', resume)
    window.removeEventListener('offline', offline)
    window.removeEventListener('auth:unauthorized', stop)
    document.removeEventListener('visibilitychange', resume)
  }
  window.addEventListener('online', resume)
  window.addEventListener('offline', offline)
  window.addEventListener('auth:unauthorized', stop)
  document.addEventListener('visibilitychange', resume)
  connect()
  return stop
}
